import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getOrganizationContext } from './orgContext';
import './CustomerLayout.css';
import { filterNavSections, getUserRoles, mergeRoles, studentNavigationSections } from './navigationConfig';

interface CustomerLayoutProps {
  children: React.ReactNode;
  autoCollapse?: boolean;
}

const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children, autoCollapse = false }) => {
  const { isAuthenticated, user, logout } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(autoCollapse);

  const organizationContext = getOrganizationContext();
  const orgSlug = organizationContext?.organizationSlug ?? '';
  const orgName = organizationContext?.organizationName ?? orgSlug ?? 'My Portal';

  const orgAwarePath = (path: string) =>
    orgSlug ? `/${orgSlug}${path}` : path;

  const userDisplayName =
    user?.name || user?.nickname || user?.email || '';
  const userRoles = useMemo(() => mergeRoles(getUserRoles(user), []), [user]);
  const visibleSections = useMemo(() => filterNavSections(studentNavigationSections, isAuthenticated, userRoles), [isAuthenticated, userRoles]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    setSidebarOpen(false);
    setDesktopCollapsed(autoCollapse);
  }, [location.pathname, autoCollapse]);

  const normalizedPath = location.pathname.toLowerCase();

  const isLinkActive = (to: string): boolean => {
    const normalizedTo = to.toLowerCase();
    if (normalizedPath === normalizedTo) return true;
    return normalizedPath.startsWith(`${normalizedTo}/`);
  };

  const showOverlaySidebar = isMobile || desktopCollapsed;

  if (!isAuthenticated) {
    return <>{children}</>;
  }

  return (
    <div className={`customer-shell${desktopCollapsed ? ' sidebar-collapsed' : ''}`}>
      {isMobile && (
        <header className="customer-mobile-topbar">
          <button
            type="button"
            className="customer-mobile-menu-btn"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            {sidebarOpen ? 'Close' : 'Menu'}
          </button>
          <div className="customer-mobile-title">{orgName}</div>
          <button
            type="button"
            className="customer-mobile-back-link"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => navigate('/')}
          >
            Home
          </button>
        </header>
      )}

      <div
        className={`customer-sidebar-backdrop ${sidebarOpen ? 'is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      <aside className={`customer-sidebar${showOverlaySidebar ? ' overlay-mode' : ''}${sidebarOpen ? ' is-open' : ''}`}>
        <div className="customer-sidebar-header">
          <div className="customer-sidebar-title">{orgName}</div>
          <div className="customer-sidebar-subtitle">Student portal</div>
          {userDisplayName && (
            <div style={{ fontSize: '0.82rem', color: '#a5d8d3', marginTop: 4, marginBottom: 8 }}>
              {userDisplayName}
            </div>
          )}
          <Link to="/" className="customer-back-link">← Home</Link>
        </div>

        <nav className="customer-nav">
          {visibleSections.map((section) => (
            <div key={section.title} className="customer-nav-section">
              <div className="customer-nav-section-title">{section.title}</div>
              {section.links.map((item) => (
                <Link
                  key={item.to}
                  to={orgAwarePath(item.to)}
                  className={`customer-nav-link${isLinkActive(orgAwarePath(item.to)) ? ' active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <div style={{ marginTop: 'auto', paddingTop: 16 }}>
          <button
            type="button"
            className="customer-nav-link"
            style={{ width: '100%', color: '#f87171', borderColor: 'rgba(248,113,113,0.25)' }}
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="customer-main">
        {desktopCollapsed && !isMobile && (
          <button
            type="button"
            className="customer-float-toggle"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-label="Toggle navigation"
          >
            &#9776;
          </button>
        )}
        <div className="customer-content">{children}</div>
      </main>
    </div>
  );
};

export default CustomerLayout;
